import { isTabAllowed, type TabRequirement } from '@revelio/auth';
import { assistantAccessDeniedMessage } from '@revelio/core/global-ai-assistant/access-denial';
import { ASSISTANT_DISCOVERY_TARGETS } from '@revelio/core/global-ai-assistant/discovery-targets';
import { Tab } from '@revelio/data-access';

import { ANALYTICS_VIEWS } from '../CustomDash/utils/revelio-primary-filter-entity-switch';

export type GlobalAssistantAccessDecision =
  | { allowed: true }
  | {
      allowed: false;
      featureLabel: string;
      message: string;
    };

/** `company/compositions` → `compositions`. */
const segmentOfPath = (path: string): string | undefined => path.split('/')[1];

/**
 * Entitlement per page segment, DERIVED from the product nav's own view
 * definitions so the assistant can never gate a page differently from the route
 * that serves it. Screener is the one explicit entry: it is not an Analytics
 * view (it lives under Discovery), so it has no `AnalyticsView` to derive from.
 *
 * Two derived requirements are worth knowing when reading a denial message:
 * - Compositions arrives as `Tab.Overview`, which names an entitlement and NOT
 *   a page — there has been no Overview page since it was split into Summary
 *   (`Tab.EntitySummary`) and Compositions. The server enum keeps the legacy
 *   `overview` value because it is the stored grant on every account, so it
 *   cannot be renamed from the dashboard; what it actually grants is the
 *   composition (headcount/profile) data. `chart-data-source-access.ts`
 *   likewise labels `[Tab.Overview]` as "Compositions" when a chart is denied.
 * - Market Conditions arrives as `allOf: [Overview, Posting]` because the page
 *   has no data source of its own: every metric is derived from composition
 *   and postings (demand/supply ratio = active postings / profiles). That is
 *   exactly the union its own charts already enforce per metric.
 *
 * Ungated segments are absent on purpose — `getGlobalAssistantRouteAccess`
 * default-allows them, and `page-catalog-access-parity.spec.ts` asserts that no
 * catalog page reaches that branch.
 *
 * The app-level pages (Individual Discovery, Posting Discovery, Data
 * Dictionary, Data Enrichment, Resume Enrichment) are matched on their FULL
 * path by `ROUTE_ACCESS_BY_PATH`, so this by-second-segment map cannot reach
 * them at all.
 */
const ROUTE_ACCESS_BY_SEGMENT: Record<
  string,
  { tab: TabRequirement; featureLabel: string }
> = {
  ...Object.fromEntries(
    ANALYTICS_VIEWS.flatMap((view) => {
      const segments = new Set(
        Object.values(view.paths)
          .map(segmentOfPath)
          .filter((segment): segment is string => segment !== undefined)
      );
      return [...segments].map((segment) => [
        segment,
        { tab: view.serverTab, featureLabel: view.name },
      ]);
    })
  ),
  screener: { tab: Tab.Screener, featureLabel: 'Screener' },
};

/**
 * Standalone product routes whose entitlement hangs off the ROUTE rather than a
 * shared page segment, keyed by full path because they are not all top-level
 * (Data Enrichment ships under Deliverables). Each mirrors the `serverTab` its
 * route declares in `routes.tsx`, so the assistant gates a page exactly as the
 * router does.
 */
const ROUTE_ACCESS_BY_PATH: Record<
  string,
  { tab: TabRequirement; featureLabel: string; optionId: string }
> = {
  // TODO(posting-discovery): swap to Tab.PostingDiscovery once backend ships
  // the enum value — mirrors the route's piggyback in routes.tsx.
  ...Object.fromEntries(
    ASSISTANT_DISCOVERY_TARGETS.filter(
      (target) => target.filterKind === 'tdV2'
    ).map((target) => [
      target.path,
      {
        tab: Tab.TalentDiscovery,
        featureLabel: target.name,
        optionId: target.optionId,
      },
    ])
  ),
  '/deliverables/data-enrichment': {
    tab: Tab.DataEnrichment,
    featureLabel: 'Data Enrichment',
    optionId: '__std__:app:data-enrichment',
  },
  '/resume-enrichment': {
    tab: Tab.ResumeParsing,
    featureLabel: 'Resume Enrichment',
    optionId: '__std__:app:resume-enrichment',
  },
  '/data-dictionary': {
    tab: Tab.DataDictionary,
    featureLabel: 'Data Dictionary',
    optionId: '__std__:app:data-dictionary',
  },
  // Reports and Data Builder have no page-catalog entry, so the assistant
  // never NAVIGATES to them and these ids match nothing in the navigate enum.
  // They are here for the other consumer of the denied-id list: the support
  // corpus, whose "request a report" / "build a dataset" sections must be
  // withheld from an account whose nav does not carry those pages. Reports is
  // the single most-asked support topic, so leaving it ungateable would have
  // left the headline case of the entitlement gap open.
  '/report-builder': {
    tab: Tab.Reports,
    featureLabel: 'Report Builder',
    optionId: '__std__:app:report-builder',
  },
  '/reports': {
    tab: Tab.Reports,
    featureLabel: 'Reports Status',
    optionId: '__std__:app:reports-status',
  },
  '/data-builder': {
    tab: Tab.Deliverables,
    featureLabel: 'Data Builder',
    optionId: '__std__:app:data-builder',
  },
  '/data-builder-status': {
    tab: Tab.Deliverables,
    featureLabel: 'Delivery Status',
    optionId: '__std__:app:delivery-status',
  },
};

function deny(featureLabel: string): GlobalAssistantAccessDecision {
  return {
    allowed: false,
    featureLabel,
    message: assistantAccessDeniedMessage(featureLabel),
  };
}

export function getGlobalAssistantRouteAccess(
  navigatePath: string,
  userTabs: Tab[] = []
): GlobalAssistantAccessDecision {
  const segments = navigatePath.split('/').filter(Boolean);
  const [, secondSegment] = segments;

  const pathAccess = ROUTE_ACCESS_BY_PATH[`/${segments.join('/')}`];
  if (pathAccess) {
    return isTabAllowed(pathAccess.tab, userTabs)
      ? { allowed: true }
      : deny(pathAccess.featureLabel);
  }

  const routeAccess =
    secondSegment === undefined
      ? undefined
      : ROUTE_ACCESS_BY_SEGMENT[secondSegment];

  if (!routeAccess) {
    return { allowed: true };
  }

  return isTabAllowed(routeAccess.tab, userTabs)
    ? { allowed: true }
    : deny(routeAccess.featureLabel);
}

/** Entity-scoped tabs whose standard pages live under `/${entity}/${segment}`. */
const ENTITY_SCOPED_NAVIGATE_ENTITIES = [
  'company',
  'industry',
  'geography',
  'role',
] as const;

/**
 * Compute the `__std__:` navigate option ids the account CANNOT access, so the
 * backend can strip them from the `navigate` tool's candidate enum and the
 * "pages you can open" grid before the model ever sees them — and so
 * the support corpus can withhold the sections describing those pages before
 * the answering sub-model reads them.
 *
 * Default-allow: an id is included ONLY when
 * {@link getGlobalAssistantRouteAccess} confirms the route is denied. Ungated
 * segments (no `ROUTE_ACCESS_BY_SEGMENT` entry) are never denied. Over-inclusion
 * of a non-existent entity/segment combo is harmless — the backend removes by
 * exact id match — so we cover every gated entity × segment pair the backend
 * might offer.
 */
export function getDeniedStandardNavigateOptionIds(
  userTabs: Tab[] = []
): string[] {
  const denied: string[] = [];

  for (const entity of ENTITY_SCOPED_NAVIGATE_ENTITIES) {
    for (const segment of Object.keys(ROUTE_ACCESS_BY_SEGMENT)) {
      const access = getGlobalAssistantRouteAccess(
        `/${entity}/${segment}`,
        userTabs
      );
      if (!access.allowed) {
        denied.push(`__std__:${entity}:${segment}`);
      }
    }
  }

  // Standalone product routes carry their own option id (as produced by the
  // backend canonical page intents, `__std__:${entity}:${pathSegment}`) so a
  // page whose route the id cannot reconstruct — Data Enrichment under
  // Deliverables — is still stripped by the id the model would call.
  for (const [path, { optionId }] of Object.entries(ROUTE_ACCESS_BY_PATH)) {
    if (!getGlobalAssistantRouteAccess(path, userTabs).allowed) {
      denied.push(optionId);
    }
  }

  if (!getGlobalAssistantRouteAccess('/data-dictionary', userTabs).allowed) {
    denied.push('__std__:app:data-dictionary');
  }

  return denied;
}
